/**
 * useGlobalStreaming - React Hook for centralized file streaming
 * 
 * Provides easy-to-use interface for viewing and downloading files across all domains.
 * 
 * Example usage:
 * const { viewFile, downloadFile, loading, error } = useGlobalStreaming();
 * 
 * // View a task attachment
 * await viewFile({
 *   domain: 'tasks',
 *   resourceId: taskId,
 *   fileId: attachmentId,
 *   filename: 'document.pdf'
 * });
 * 
 * // Download a client document
 * await downloadFile({
 *   domain: 'clients',
 *   resourceId: clientUuid,
 *   fileId: documentId,
 *   filename: 'contract.pdf'
 * });
 */

import { useState, useCallback } from 'react';
import { 
  StreamingRequest, 
  StreamingResponse, 
  streamFile, 
  downloadFile 
} from '../services/uploads/GlobalStreamingManager';

interface UseGlobalStreamingOptions {
  onSuccess?: (response: StreamingResponse) => void;
  onError?: (error: string) => void;
}

export const useGlobalStreaming = (options?: UseGlobalStreamingOptions) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const viewFile = useCallback(
    async (req: Omit<StreamingRequest, 'action'>): Promise<StreamingResponse> => {
      setLoading(true);
      setError(null);

      try {
        const response = await streamFile({
          ...req,
          action: 'view',
        });

        if (!response.success) {
          const errorMsg = response.error || 'Failed to stream file';
          setError(errorMsg);
          options?.onError?.(errorMsg);
        } else {
          options?.onSuccess?.(response);
        }

        return response;
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMsg);
        options?.onError?.(errorMsg);
        return { success: false, error: errorMsg };
      } finally {
        setLoading(false);
      }
    },
    [options]
  );

  const downloadFileAsync = useCallback(
    async (req: Omit<StreamingRequest, 'action'>): Promise<StreamingResponse> => {
      setLoading(true);
      setError(null);

      try {
        const response = await downloadFile({
          ...req,
          action: 'download',
        });

        if (!response.success) {
          const errorMsg = response.error || 'Failed to download file';
          setError(errorMsg);
          options?.onError?.(errorMsg);
        } else {
          options?.onSuccess?.(response);
        }

        return response;
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMsg);
        options?.onError?.(errorMsg);
        return { success: false, error: errorMsg };
      } finally {
        setLoading(false);
      }
    },
    [options]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    viewFile,
    downloadFile: downloadFileAsync,
    loading,
    error,
    clearError,
  };
};

export default useGlobalStreaming;
